import type { BadgeTone } from '@/components/admin/entretiens/status-badge';
import { ApiError } from '@/lib/api';
import {
  answerFaultOf,
  answerFaultSentence,
  failureSentence,
  noyauNamed,
} from '@/lib/session-analysis';
import { sessionShortMoment } from '@/lib/session-labels';
import type {
  NoyauAnalystCall,
  NoyauBlockMode,
  NoyauCardRole,
  NoyauCode,
  NoyauDetermination,
  NoyauDeterminationState,
  NoyauDraw,
  NoyauTrialRefusalCode,
  NoyauTrialSource,
  NoyauTrialStartSource,
  NoyauTrialSummary,
  RangementTrialSummary,
  SessionDetail,
  SessionNoyauChoiceReading,
} from '@matter_extractor/shared';
import { noyauOf, noyauTrialErrorSchema } from '@matter_extractor/shared';

/**
 * What Sessions SAYS of the noyau (noyau.md §9.1, §9.2; issue #152) - pure, in
 * one module beside the screens that draw it, the régime of `session-labels.ts`.
 *
 * ## The operator's words, never the person's
 *
 * These screens name the noyaux, write their triplets and paint their colours:
 * the operator sees them, the person never did (N19, N28). The words
 * « principal », « second », « retenu » and « opposé » are written here and
 * nowhere a person reads.
 *
 * ## Nothing here counts
 *
 * The cost and the tokens of the analyst are in the eight tiles already, added
 * by `sessionFigures` of `shared` (#149). This module arranges what the reading
 * of the server serves - it derives no euro and no noyau.
 */

/* ------------------------------------------------------------------ names */

/** « Souverain Magicien (SGP) » - the deposited name, then the triplet. Said once, in `session-analysis.ts`. */
export const noyauLabel: (code: NoyauCode) => string = noyauNamed;

/* ------------------------------------------------------------------ the pill */

/**
 * The ink of the pill a line of the list writes its triplet in (§9.2): white,
 * or the gray-900 of the charte where white would not read - the yellow of the
 * Producteur Séducteur, the green of the Producteur Réinventeur, and the light
 * blue of the Souverain Juriste (maquette `SessionListe`, « blanc ou foncé
 * selon le contraste »).
 *
 * A table and not a computation, for the one reason this package cannot write
 * a colour: the two inks are TOKENS (`text-white`, `text-gray-900`), and
 * `design-system-adherence.test.ts` refuses a hex outside `styles.css`. The
 * choice is the WCAG arithmetic's all the same - `session-noyau.test.ts`
 * measures each official colour against both inks and holds that the one named
 * here is the better of the two.
 */
export const NOYAU_PILL_INK: Readonly<Record<NoyauCode, 'light' | 'dark'>> = {
  SGP: 'light',
  SPG: 'dark',
  GSP: 'light',
  GPS: 'light',
  PGS: 'dark',
  PSG: 'dark',
};

/** The token class of that ink. */
export function noyauPillInkClass(code: NoyauCode): string {
  return NOYAU_PILL_INK[code] === 'light' ? 'text-white' : 'text-gray-900';
}

/* ------------------------------------------------------ the determination */

/** The four states of §4.5, as the badge of the block says them. */
export const DETERMINATION_STATE_LABEL: Readonly<Record<NoyauDeterminationState, string>> = {
  pending: 'En attente',
  rendered: 'Rendue',
  'no-extracts': 'Sans extrait',
  failed: 'En échec',
};

/**
 * Its tone, on the register every pill of Sessions uses. A skip is not an
 * error of the person's: `no-extracts` is neutral, and `failed` - a defect of
 * the dispositif - is yellow, the colour the tiles give their two defects.
 */
export function determinationTone(state: NoyauDeterminationState): BadgeTone {
  if (state === 'rendered') return 'success';
  if (state === 'pending') return 'info';
  if (state === 'failed') return 'warning';
  return 'neutral';
}

/**
 * The sentence a determination that is not rendered says in place of its notes
 * (§9.1: « la phrase qui le dit »), or null on a rendered one.
 *
 * A failure WITHOUT a call is its own sentence: a run that threw before the
 * analyst was reached (`settleEmptyFailure`) failed no attempt, and « à chacun
 * de ses essais » would blame calls that never happened. Since #196 a failure with
 * no call can also have a REASON - the session froze no complete instrument, it was
 * started before the analyst in two passes - and then says it
 * (`analysis.failure`, `failureSentence`). A failure that did call names the pass
 * that failed: the first, the second never leaving.
 */
export function determinationSentence(
  determination: Pick<NoyauDetermination, 'state' | 'calls'> &
    Partial<Pick<NoyauDetermination, 'analysis'>>,
): string | null {
  switch (determination.state) {
    case 'pending':
      return 'L’analyste tourne encore, en fond : la personne attend au bouton, « Un instant... ».';
    case 'no-extracts':
      return 'Aucun extrait vivant à la clôture du protocole : aucun appel n’a été passé, et la séquence du noyau a été sautée.';
    case 'failed': {
      const failure = determination.analysis?.failure ?? null;
      if (determination.calls.length === 0) {
        return failure === null
          ? 'La détermination a échoué avant tout appel à l’analyste : la séquence du noyau a été sautée, et la personne n’a vu aucune carte.'
          : failureSentence(failure);
      }
      const pass = determination.analysis == null ? null : determination.calls.at(-1)?.pass;
      return pass === null || pass === undefined
        ? 'L’analyste a échoué à chacun de ses essais : la séquence du noyau a été sautée, et la personne n’a vu aucune carte.'
        : `La passe ${pass} de l’analyste a échoué à chacun de ses essais : la séquence du noyau a été sautée, et la personne n’a vu aucune carte.`;
    }
    default:
      return null;
  }
}

/**
 * « essai », « essais » - a call and its retry (N4), the word beside the count in
 * bold on a determination of before #196 and on the block of the rangement. The
 * analyst in two passes counts « appels », by pass (`appelsWord`).
 */
export function attemptsWord(count: number): string {
  return count >= 2 ? 'essais' : 'essai';
}

/**
 * How long the determination took, from its launch - on entering the farewell
 * step, or at the close of a protocol without one (`noyau.md` §4.4) - to its
 * settlement: at most what the person may have waited at the button (§4.6). Null while
 * it runs, and on a stamp that does not parse.
 */
export function determinationDurationMs(determination: NoyauDetermination): number | null {
  if (determination.settledAt === null) return null;
  const started = Date.parse(determination.startedAt);
  const settled = Date.parse(determination.settledAt);
  if (Number.isNaN(started) || Number.isNaN(settled)) return null;
  return Math.max(0, settled - started);
}

/**
 * « 18 s », « 2 min 05 », or « moins d’une seconde ». With `unit` the minutes keep
 * their unit - « 2 min 05 s » - as the planches of the analyst in two passes write
 * them (`ParcoursAvantApres`); the block of the rangement and a determination of
 * before #196 keep the form they had.
 */
export function secondsLabel(durationMs: number, unit = false): string {
  const seconds = Math.round(durationMs / 1000);
  if (seconds === 0) return 'moins d’une seconde';
  if (seconds < 60) return `${seconds} s`;
  const rest = String(seconds % 60).padStart(2, '0');
  return `${Math.floor(seconds / 60)} min ${rest}${unit ? ' s' : ''}`;
}

/** The model the API answered on the LAST call that got that far - the one the notes came from. */
export function renderedModel(determination: NoyauDetermination): string | null {
  for (const call of [...determination.calls].reverse()) {
    if (call.model !== null) return call.model;
  }
  return null;
}

/**
 * Why one attempt of the analyst did not give the notes (§9.1: « pour l'échec,
 * la cause de chaque essai »), or null on the attempt that did.
 *
 * A call that never came back says what the provider said. A call that came
 * back UNUSABLE is read again here, the way the Brut layer of a turn reads its
 * scribe's answer: not JSON, or JSON that is not six notes - two different
 * repairs, so two different sentences.
 *
 * What is read of a call that came back is `answerFaultOf`'s, in
 * `session-analysis.ts`: the same two readings before the parse (a cut answer,
 * a ``` fence the server forgives) and, since #196, the shape each PASS asks for -
 * the first answers `objectives`, the second `styles`, a call of before #196 six
 * `scores`.
 */
export function attemptCause(call: NoyauAnalystCall): string | null {
  if (call.failedReason !== null) return `L’appel n’a pas abouti : ${call.failedReason}`;
  const fault = answerFaultOf(call);
  return fault === null ? null : answerFaultSentence(fault);
}

/**
 * The tie a draw settled (§4.2, N2), in a sentence: which noyaux shared a note,
 * and which one the draw put ahead.
 */
export function drawSentence(draw: NoyauDraw): string {
  const tied = draw.tied.map((code) => noyauOf(code).name).join(', ');
  const [first] = draw.order;
  const winner = first === undefined ? '' : ` : ${noyauOf(first).name} passe devant`;
  const place = draw.place === 'principal' ? 'la première place' : 'la seconde place';
  return `Égalité pour ${place} entre ${tied}, départagée par tirage${winner}.`;
}

/* ------------------------------------------------------------ the trial */

/**
 * The states of a TRIAL of the analyst (noyau.md §9.4, #164) on the badge of
 * its block: a trial runs in front of the operator, so it is « en cours » where
 * a determination the person waited on was « en attente ».
 */
export const TRIAL_STATE_LABEL: Readonly<Record<NoyauDeterminationState, string>> = {
  ...DETERMINATION_STATE_LABEL,
  pending: 'En cours',
};

/**
 * The sentence a trial says in place of its notes, or null once rendered. No
 * word of a sequence being skipped: a trial skips nothing and nobody saw it.
 *
 * It waits for TWO calls since #196 (analyste.md §11.4): one per pass, the second
 * leaving once the first has been read. The wait is said in words and not in a
 * figure - what it lasts is measured on the Train (§16), and a number written here
 * before that would be a guess.
 */
export function trialSentence(
  determination: Pick<NoyauDetermination, 'state' | 'calls'> &
    Partial<Pick<NoyauDetermination, 'analysis'>>,
  source: NoyauTrialSource = { kind: 'session' },
): string | null {
  switch (determination.state) {
    case 'pending':
      return source.kind === 'review'
        ? 'L’analyste lit la relecture de cette session en deux passes, un appel chacune : les notes arrivent dans quelques minutes.'
        : 'L’analyste lit la matière de cette session en deux passes, un appel chacune : les notes arrivent dans quelques minutes.';
    case 'failed': {
      if (determination.calls.length === 0) {
        // A trial settles `failed` with no call for one reason of its own: the development side
        // holds no version of Méthode (analyste.md §9.2), which its analysis says.
        return determination.analysis?.failure?.reason === 'method-missing'
          ? 'L’essai a échoué avant tout appel : Méthode n’a aucune version en développement.'
          : 'L’essai a échoué avant tout appel à l’analyste.';
      }
      const pass = determination.analysis == null ? null : determination.calls.at(-1)?.pass;
      return pass === null || pass === undefined
        ? 'L’analyste n’a pas rendu six notes lisibles, relance comprise.'
        : `La passe ${pass} de l’analyste n’a rien rendu de lisible, relance comprise.`;
    }
    default:
      return null;
  }
}

/**
 * What the list of a session's trials says of one (issue #167): what came out -
 * the principal and the second with their notes, or its state - and the
 * fingerprint of the reading it was served, read on its last call. The moment,
 * the version and the cost are written by the row, beside these.
 */
export function trialOutcome(trial: Pick<NoyauTrialSummary, 'determination'>): string {
  const { determination } = trial;
  if (determination.state !== 'rendered') return TRIAL_STATE_LABEL[determination.state];
  const noteOf = (code: NoyauCode | null) =>
    code === null
      ? null
      : `${code} ${determination.scores.find((score) => score.noyau === code)?.score ?? '?'}`;
  return [noteOf(determination.principal), noteOf(determination.second)]
    .filter((part) => part !== null)
    .join(' · ');
}

/* ------------------------------------------------- the source of a trial */

/** What the selector calls the matter of the session, its default (relecture.md §11). */
export const NOYAU_SOURCE_SESSION_LABEL = 'Matière de la session';

/** One line of the selector « Source » of the blank analyst. */
export interface NoyauSourceChoice {
  /** Stable, and what the `<option>` carries: never the label, which is French. */
  readonly key: string;
  readonly label: string;
  /** What `POST .../noyau/trials` is asked for. */
  readonly source: NoyauTrialStartSource;
}

/** The first line of the selector, always there, and what a bare press reads. */
export const NOYAU_SOURCE_SESSION_CHOICE: NoyauSourceChoice = {
  key: 'session',
  label: NOYAU_SOURCE_SESSION_LABEL,
  source: { kind: 'session' },
};

/** « Rangement à blanc du 12 oct. 10:02 (v14) » - a blank rangement, by its hour and its consigne. */
export function rangementSourceLabel(
  trial: Pick<RangementTrialSummary, 'startedAt' | 'protocolVersion'>,
): string {
  return `Rangement à blanc du ${sessionShortMoment(trial.startedAt)} (v${trial.protocolVersion.version})`;
}

/** « Relecture (10 extraits) » - proposed as soon as the review holds one. */
export function reviewSourceLabel(extractCount: number): string {
  return `Relecture (${extractCount} ${extractCount > 1 ? 'extraits' : 'extrait'})`;
}

/**
 * The lines of the selector, in the order of §11: the matter of the session -
 * always, and the default -, then each blank rangement that RENDERED (a pending
 * or failed one cannot be read: it is absent, §16), newest first as the list
 * serves them, then the review once it holds an extract.
 */
export function noyauSourceChoices(
  rangementTrials: readonly Pick<
    RangementTrialSummary,
    'id' | 'state' | 'startedAt' | 'protocolVersion'
  >[],
  reviewExtractCount: number,
): NoyauSourceChoice[] {
  const choices: NoyauSourceChoice[] = [NOYAU_SOURCE_SESSION_CHOICE];
  for (const trial of rangementTrials) {
    if (trial.state !== 'rendered') continue;
    choices.push({
      key: `rangement-trial:${trial.id}`,
      label: rangementSourceLabel(trial),
      source: { kind: 'rangement-trial', trialId: trial.id },
    });
  }
  if (reviewExtractCount > 0) {
    choices.push({
      key: 'review',
      label: reviewSourceLabel(reviewExtractCount),
      source: { kind: 'review' },
    });
  }
  return choices;
}

/**
 * What a row of « Essais de cette session » says of the source: « sur la
 * matière », « sur le rangement à blanc du 12 oct. 10:02 », « sur la relecture ».
 */
export function trialSourceLabel(source: NoyauTrialSource): string {
  switch (source.kind) {
    case 'session':
      return 'sur la matière';
    case 'rangement-trial':
      return `sur le rangement à blanc du ${sessionShortMoment(source.startedAt)}`;
    case 'review':
      return 'sur la relecture';
  }
}

/**
 * The line « Matière : » of a trial's block: what the analyst READ. On the
 * targets the extracts of the source, elsewhere the scribe's - a trial on the
 * matter of the session reads its rangement when it is rendered, as the
 * determination does (scenario-succes.md §6).
 */
export function trialSourceMatter(source: NoyauTrialSource): string {
  switch (source.kind) {
    case 'session':
      return 'la matière de la session : son rangement quand il est rendu, le scribe ailleurs';
    case 'rangement-trial':
      return `le rangement à blanc du ${sessionShortMoment(source.startedAt)} (consigne v${source.version}) sur les cibles, le scribe ailleurs`;
    case 'review':
      return `la relecture de la session, état du ${sessionShortMoment(source.asOf)} (${source.extractCount} ${source.extractCount > 1 ? 'extraits' : 'extrait'}), et le scribe ailleurs`;
  }
}

/**
 * The fingerprint a row of the list of trials says: the INSTRUMENT's - the two
 * systems together, what the measure groups by (K11) - and, on a trial of before
 * #196, which has none, the system its last call was served. Null before any call.
 */
export function trialFingerprint(trial: Pick<NoyauTrialSummary, 'determination'>): string | null {
  const { determination } = trial;
  return (
    determination.analysis?.instrument ?? determination.calls.at(-1)?.promptFingerprint ?? null
  );
}

/**
 * Why a trial was refused, in the operator's words - worded from the `code`, never
 * the message. The two codes of the section Analyste (`analyst-levels-missing`,
 * `analyst-reading-missing`) have a door beside them, which only a screen that
 * knows the protocol can draw (`walk-noyau-trial.tsx`).
 */
export const NOYAU_TRIAL_REFUSAL_SENTENCE: Readonly<Record<NoyauTrialRefusalCode, string>> = {
  'no-protocol':
    'Cette session n’a joué aucun protocole lisible : l’analyste n’a pas de matière à lire.',
  'no-extracts': 'Cette session n’a aucun extrait vivant : l’analyste n’aurait rien à lire.',
  'protocol-retired':
    'Le protocole de cette session est retiré : sa version de développement ne se lit plus.',
  'analyst-reading-missing':
    'La version de développement du protocole a une lecture vide dans sa section Analyste : la passe 1 ou la passe 2 n’aurait rien pour lire.',
  'analyst-levels-missing':
    'La version de développement du protocole n’a pas un objectif à chacun des trois niveaux de l’analyste.',
  'no-review-extracts':
    'La relecture de cette session ne porte aucun extrait : l’analyste n’aurait rien à lire.',
  'rangement-trial-unusable':
    'Ce rangement à blanc n’est pas un essai rendu de cette session : l’analyste ne peut pas le lire.',
};

/** The code of a trial's refusal, or null on any other failure - a 409 whose body is the contract. */
export function noyauTrialRefusalOf(error: unknown): NoyauTrialRefusalCode | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const refusal = noyauTrialErrorSchema.safeParse(error.details);
  return refusal.success ? refusal.data.code : null;
}

/* -------------------------------------------------------------- the pages */

/** What a card was in its choice (§9.1), as its pill says it. */
export const CARD_ROLE_LABEL: Readonly<Record<NoyauCardRole, string>> = {
  principal: 'principal',
  second: 'second',
  retained: 'retenu',
  opposite: 'opposé',
};

/** The same, inside a sentence: « Carte prise : ..., le principal. » */
export const CARD_ROLE_ARTICLE: Readonly<Record<NoyauCardRole, string>> = {
  principal: 'le principal',
  second: 'le second',
  retained: 'le retenu',
  opposite: 'l’opposé',
};

/** The three modes of §3.2, as the line of a page names them. */
export const NOYAU_MODE_LABEL: Readonly<Record<NoyauBlockMode, string>> = {
  'first-choice': 'premier choix',
  'second-choice': 'second choix',
  portrait: 'portrait',
};

/** What a traversed page carried of the sequence: one Noyau block, and its choice read. */
export type WalkNoyauSite = {
  readonly blockId: string;
  readonly mode: NoyauBlockMode;
  /** The choice of that page, dealt; null on a portrait, and on a choice never dealt. */
  readonly choice: SessionNoyauChoiceReading | null;
};

/**
 * EVERY Noyau block of one traversed page (§9.1), the choice first - or none.
 *
 * Read off the sites of the FROZEN experience the reading serves: a visit
 * records a page and nothing of what it carried, so the server says where each
 * Noyau block sits and the parcours matches them by page. All of them and not
 * the first: `experienceProblems` lets a portrait share a page with a choice,
 * and a portrait posed at the page's first temps would otherwise have hidden
 * the cards, the order shown and the card taken. The choice leads because it is
 * what the page was LEFT by; the rest keep their reading order.
 */
export function noyauSitesOfPage(detail: SessionDetail, pageId: string): WalkNoyauSite[] {
  const noyau = detail.noyau;
  if (noyau === null) return [];
  const sites = noyau.blocks
    .filter((one) => one.pageId === pageId)
    .map((site) => ({
      blockId: site.blockId,
      mode: site.mode,
      choice:
        site.mode === 'portrait'
          ? null
          : (noyau.choices.find((one) => one.mode === site.mode && one.pageId === pageId) ?? null),
    }));
  const leads = (site: WalkNoyauSite): number => (site.mode === 'portrait' ? 1 : 0);
  return sites.sort((one, other) => leads(one) - leads(other));
}

/** The card a choice was left by, or null while nobody confirmed one. */
export function takenCard(
  choice: SessionNoyauChoiceReading,
): SessionNoyauChoiceReading['cards'][number] | null {
  return choice.cards.find((card) => card.cardId === choice.takenCardId) ?? null;
}
